/* eslint-disable no-console -- the error link logs GraphQL and network errors, as the Apollo Client 2 setup did */
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import { ServerError } from '@apollo/client/errors';
import type { GraphQLFormattedError } from 'graphql';
import get from 'lodash/get';
import { from, map, mergeMap, tap } from 'rxjs';
import { backendErrors as enBackendErrors } from 'i18n/en';
import { backendErrors as deBackendErrors } from 'i18n/de';
import RefreshTokenService from 'services/RefreshTokenService';
import { afterLogout } from 'hooks/auth/useLogout';
import isRecord from 'utils/isRecord';
import requireValue from 'utils/requireValue';
import { LogoutDocument } from 'graphql/generated/graphql';
import settings from '../config/settings';

/** The logout must not wait for, or start, another logout when its own token refresh fails. */
const LOGOUT_OPERATION = 'Logout';

interface ITranslatedError extends GraphQLFormattedError {
  originalMessage?: string;
}

let logoutPromise: Promise<void> | null = null;

/** Ends the session once, even when several operations ask for it at the same time. */
const triggerLogout = (): Promise<void> => {
  if (!logoutPromise) {
    logoutPromise = (async () => {
      try {
        await client.mutate({ mutation: LogoutDocument });
      } catch (e) {
        console.log('Error while logout', e);
      }
      afterLogout(client);
    })().finally(() => {
      logoutPromise = null;
    });
  }
  return logoutPromise;
};

/** After a failed refresh: the operations that waited for it go on first, then the session ends. */
const scheduleLogout = () => {
  window.setTimeout(() => {
    window.setTimeout(() => {
      triggerLogout();
    });
  });
};

let refreshPromise: Promise<void> | null = null;

/**
 * Single-flight refresh (it replaces apollo-link-token-refresh): operations sent while the access token is expired
 * wait for one `token` request and are sent afterwards, also when the refresh failed (the session then ends, see
 * scheduleLogout).
 */
const runRefresh = (): Promise<void> => {
  if (!refreshPromise) {
    refreshPromise = RefreshTokenService.fetchNewAccessToken()
      .then(() => {
        console.log('refreshed a token with apollo TokenRefreshLink');
      })
      .catch(() => {
        scheduleLogout();
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

/** The server needs a valid access token to end the session; a failed refresh must not start a second logout. */
const refreshWithoutLogout = (): Promise<void> =>
  RefreshTokenService.fetchNewAccessToken().then(
    () => undefined,
    () => undefined,
  );

const authLink = new ApolloLink((operation, forward) => {
  if (RefreshTokenService.isTokenValidOrUndefined()) return forward(operation);
  const refresh =
    operation.operationName === LOGOUT_OPERATION ? refreshWithoutLogout() : runRefresh();
  return from(refresh).pipe(mergeMap(() => forward(operation)));
});

/** The headers of an operation context, which types its values as any. */
const headersOf = (context: Record<string, unknown>): unknown => context.headers;

/** Every request tells the backend that it comes from the admin (formerly setContext of apollo-link-context). */
const platformLink = new ApolloLink((operation, forward) => {
  operation.setContext((previousContext) => {
    const headers = headersOf(previousContext);
    // Spreading a missing value adds nothing.
    return { headers: { ...(isRecord(headers) ? headers : {}), platform_type: 'admin' } };
  });
  return forward(operation);
});

/**
 * Backend error codes become the translation of the active UI language, the original stays in originalMessage
 * (the in-place translation of the Apollo Client 2 setup, on a copy).
 */
const translateError = (error: GraphQLFormattedError): ITranslatedError => {
  const locale = window?.localStorage?.getItem('language') || settings.defaultLocale;
  const backendErrorsTranslations = { de: deBackendErrors, en: enBackendErrors };
  const messageKey = `${locale}.${error.message}`;
  const translated: unknown = get(backendErrorsTranslations, messageKey, error.message);
  if (translated === messageKey) return error;
  // A message that names a group of translations (an object, not a string) stays the message; the Apollo Client 2
  // setup put the object there. No backend error code names a group.
  const message = typeof translated === 'string' ? translated : error.message;
  return { ...error, originalMessage: error.message, message };
};

/** The console message of the Apollo Client 2 setup. */
const logGraphqlError = ({ message, locations, path, extensions }: GraphQLFormattedError) => {
  // graphql types the extensions' values as unknown; a missing extensions object throws, as before.
  const { code, exception } = requireValue(extensions, 'code');
  const stacktraceItems: unknown = get(exception, 'stacktrace', []);
  if (!Array.isArray(stacktraceItems)) throw new TypeError('stacktraceItems.map is not a function');
  const stacktrace = stacktraceItems.map((item: unknown) => item).join('\n');
  const location = JSON.stringify(locations);
  console.error(
    `[GraphQL error]: Message: ${message}, Code: ${code}, Location: ${location}, Stacktrace ${stacktrace} Path: ${path}`,
  );
};

/** GraphQL errors in the JSON body of a non-2xx response (Apollo Client 2 passed them to onError too). */
const serverErrorsOf = (error: unknown): GraphQLFormattedError[] | null => {
  if (!ServerError.is(error)) return null;
  try {
    const body: unknown = JSON.parse(error.bodyText);
    if (!isRecord(body) || !Array.isArray(body.errors)) return null;
    return body.errors.filter(
      (item): item is GraphQLFormattedError => isRecord(item) && typeof item.message === 'string',
    );
  } catch {
    return null;
  }
};

const errorLink = new ApolloLink((operation, forward) =>
  forward(operation).pipe(
    // GraphQL errors: translated first (the callers see the translation), then logged, as before.
    map((result) => {
      if (!result.errors) return result;
      const errors = result.errors.map(translateError);
      // Reading `errors[0]` throws for an empty list (as `response?.errors[0]` did).
      const errorCode: unknown = requireValue(errors, '0')[0]?.extensions?.code;
      if (errorCode === 'FORBIDDEN') {
        // Bug: the logout route is under PUBLIC_URL (stage serves the admin under /admin), the redirect ignores it;
        // kept as it was.
        window.location.replace(`${window.location.origin}/logout`);
      }
      errors.forEach(logGraphqlError);
      return { ...result, errors };
    }),
    tap({
      error: (error: unknown) => {
        const graphQLErrors = serverErrorsOf(error);
        if (graphQLErrors) graphQLErrors.map(translateError).forEach(logGraphqlError);
        console.log(`[Network error]: ${String(error)}`);
      },
    }),
  ),
);

const httpLink = new HttpLink({
  uri: settings.graphqlServerUrl,
  credentials: 'include',
});

const cacheIndexBlacklist: string[] = [];

const client: ApolloClient = new ApolloClient({
  link: ApolloLink.from([authLink, platformLink, errorLink, httpLink]),
  cache: new InMemoryCache({
    // The cache keys of the Apollo Client 2 setup: <__typename>___<_id>.
    dataIdFromObject: (object) => {
      const { __typename, _id } = object;
      if (__typename !== undefined && cacheIndexBlacklist.includes(__typename)) return undefined;
      return _id ? `${__typename}___${String(_id)}` : undefined;
    },
  }),
  // Apollo Client 2 did not re-render watching components with loading: true while they refetch.
  defaultOptions: { watchQuery: { notifyOnNetworkStatusChange: false } },
  // Formerly freezeResults on the cache and assumeImmutableResults on the client.
  assumeImmutableResults: true,
});

export default client;
